'use client';
import { useEffect, useRef, useState } from 'react';

/**
 * 숫자가 **굴러가며** 바뀐다 — 총 결제금액이 할인가로 내려가는 것이 눈에 보이게
 * (2026-08-24 사용자 지시 "총 결제금액이 띠리링 돌아가면서 할인된 금액으로").
 *
 * **굴리는 때는 깎이는 금액이 늘 때다.** 처음에는 값이 바뀌기만 하면 굴렸는데, 단가를
 * 고르거나 체크를 건드릴 때마다 돌아 정신이 없었다(2026-08-25 제보). 굴림은 "더 깎였다" 를
 * 알리는 신호지 값이 바뀌었다는 표시가 아니다.
 *
 * 그래서 **할인액(`level`)이 늘었을 때만** 굴린다 —
 *  · 혜택이 처음 붙을 때(0 → 얼마)
 *  · 이미 붙어 있는데 **혜택 상품을 더 담아 할인이 커질 때**(2026-08-26 사용자 추가)
 * 반대로 할인이 줄거나 그대로면 조용히 바꾼다. 혜택 없이 값만 달라지는 것도 마찬가지다.
 *
 * 페이지에 들어선 순간 이미 조건이 맞는 경우(이 상품만으로 성립)도 0 에서 는 것이라 굴린다 —
 * 깎였다는 것을 한 번은 보여 줘야 한다.
 *
 * **천천히 떨어진다**(2026-08-24 사용자 지시 "더 극적으로 보이기 위해") — 1초에
 * easeInOutCubic 로, 첫 굴림은 0.2초 뜸을 들여 원래 값을 먼저 보여 준 뒤 내려간다.
 * 체크를 눌러 붙은 것은 뜸 없이 바로 반응한다 — 조작에는 즉시 답해야 한다.
 *
 * 접근성 — 움직임을 싫어하는 설정(`prefers-reduced-motion`)이면 굴리지 않고 바로 바꾼다.
 */
export function useRolling(value: number, { from, level = 1, ms = 1000 }: {
  /** 굴림을 시작할 값 — 보통 할인 전 금액. 들어서자마자 굴릴 때 쓴다 */
  from?: number;
  /** 지금 깎이는 금액 — 이 값이 **늘었을 때만** 굴린다(0 에서 느는 것도 포함) */
  level?: number;
  ms?: number;
} = {}): number {
  const [shown, setShown] = useState(from ?? value);
  const raf = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const startVal = useRef(from ?? value);
  /** 첫 굴림인가 — 들어선 직후에는 잠깐 원래 값을 보여 준 뒤 떨어뜨린다(더 극적으로) */
  const first = useRef(true);
  /** 직전 할인액 — 첫 렌더는 0 으로 두어 들어서자마자 붙어 있어도 굴림이 된다 */
  const wasLevel = useRef(0);
  /**
   * 이번 (값, 할인액) 짝에 대한 **판단을 기억한다**.
   *
   * StrictMode 는 개발에서 효과를 두 번 돌린다 — 첫 번째가 "늘었다" 로 판단하고 `wasLevel`
   * 을 올려 버리면, 두 번째는 "그대로" 로 읽어 방금 예약한 굴림을 취소하고 값만 바꾼다.
   * 그래서 **들어서자마자 굴러야 할 때 아무 일도 없었다**(2026-08-26 제보).
   * 같은 짝으로 다시 들어오면 판단을 새로 하지 않고 그대로 쓴다.
   */
  const decided = useRef<{ key: string; grew: boolean; begin: number; delay: number } | null>(null);

  useEffect(() => {
    const reduce = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const key = `${value}:${level}`;
    if (decided.current?.key !== key) {
      decided.current = {
        key,
        grew: level > wasLevel.current, // 깎이는 금액이 **늘었나** — 짝마다 한 번만 센다
        begin: startVal.current,
        // 들어선 직후에는 잠깐 원래 값을 보여 준 뒤 떨어뜨린다(더 극적으로).
        // 체크를 눌러 늘어난 것은 뜸 없이 바로 — 조작에는 즉시 답해야 한다.
        delay: first.current ? 200 : 0,
      };
      wasLevel.current = level;
      first.current = false;
    }
    const { grew, begin, delay } = decided.current;

    if (reduce || !grew || begin === value) {
      setShown(value);
      startVal.current = value;
      return;
    }

    let startedAt = 0;
    const step = (now: number) => {
      if (!startedAt) startedAt = now;
      const p = Math.min(1, (now - startedAt) / ms);
      /*
        easeInOutCubic — 살짝 뜸을 들이다 **가운데서 빠르게 떨어지고** 사뿐히 멈춘다.
        easeOutQuint 는 시작부터 확 떨어지고 끝이 늘어져 1초에는 멈춰 선 것처럼 보였다.
      */
      const eased = p < 0.5 ? 4 * p ** 3 : 1 - (-2 * p + 2) ** 3 / 2;
      setShown(Math.round(begin + (value - begin) * eased));
      if (p < 1) raf.current = requestAnimationFrame(step);
      else startVal.current = value;
    };
    timer.current = setTimeout(() => { raf.current = requestAnimationFrame(step); }, delay);
    return () => { clearTimeout(timer.current); cancelAnimationFrame(raf.current); };
  }, [value, level, ms]);

  return shown;
}
